import React from 'react';
import PropTypes from 'prop-types';

import { Context } from 'internal/Provider';

import { eventHasKey, KEY } from 'utilsTS/dom';
import { useBrowser, useLatest, useRefs } from 'utilsTS/hooks';
import { omitProps } from 'utilsTS/react';
import { ref as PropTypesRef } from 'utilsTS/prop-types';

import { useClickOutsideStack } from './useClickOutsideStack';

export interface ClickOutsideProps extends React.ComponentPropsWithoutRef<'div'> {
    children: React.ReactNode;
    innerRef?: React.Ref<HTMLDivElement>;

    /** Accepts a tag or a component replacing the wrapper */
    tag?: React.ElementType;
    /** Defines id attribute */
    id?: string;
    /** Defines whether the functionality is activated */
    active?: boolean;
    /** Defines whether an ESC keyboard event should trigger */
    triggerOnEsc?: boolean;
    /** Defines whether a click event should trigger */
    triggerOnClick?: boolean;
    /** Gets called on click events and can prevent onTrigger from being called by returning false */
    isClickOutside?: (event: MouseEvent) => boolean;
    /** Is called on every keyboard/click event */
    onTrigger: (event: MouseEvent | KeyboardEvent, isClick: boolean) => void;
}

export const ClickOutside: React.FC<ClickOutsideProps> = ({
    innerRef,
    tag: Tag = 'div',
    id,
    active = true,
    triggerOnEsc = true,
    triggerOnClick = true,
    isClickOutside = () => true,
    onTrigger,
    children,
    ...rest
}) => {
    const ref = React.useRef<HTMLElement>();
    const activeRef = useLatest(active);
    const triggerOnEscRef = useLatest(triggerOnEsc);
    const triggerOnClickRef = useLatest(triggerOnClick);
    const isCurrent = useClickOutsideStack(id, active);
    const { document } = useBrowser(React.useContext(Context));
    const refs = useRefs(ref, innerRef);

    React.useEffect(() => {
        if (!document || !active || !isCurrent) {
            return () => {};
        }

        const handleClick = (event: MouseEvent) => {
            const { target, type } = event;
            const isClick = type === 'mousedown';

            if (!isClickOutside(event)) return;
            if (!triggerOnClickRef.current) return;
            if (!activeRef.current) return;
            if (!ref.current) return;
            if (ref.current.contains(target as Node)) return;

            onTrigger(event, isClick);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (!triggerOnEscRef.current) return;
            if (!activeRef.current) return;

            if (eventHasKey(event, KEY.ESC)) {
                onTrigger(event, false);
            }
        };

        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKeyDown, {
            // use capture phase to prevent events not being triggered when event.preventDefault()
            // is called in target https://javascript.info/bubbling-and-capturing#capturing
            capture: true,
            passive: true,
        });

        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKeyDown, {
                capture: true,
            });
        };
        // the listeners need to be rebound on prop changes because of variable scoping in their handlers
    }, [
        isCurrent,
        active,
        activeRef,
        document,
        isClickOutside,
        onTrigger,
        triggerOnClickRef,
        triggerOnEscRef,
    ]);

    return (
        <Tag {...omitProps(rest, ClickOutside)} ref={refs} id={id}>
            {children}
        </Tag>
    );
};

ClickOutside.displayName = 'ClickOutside';
ClickOutside.propTypes = {
    innerRef: PropTypesRef(),
    tag: PropTypes.elementType as React.Validator<React.ElementType>,
    active: PropTypes.bool,
    triggerOnEsc: PropTypes.bool,
    triggerOnClick: PropTypes.bool,
    isClickOutside: PropTypes.func,
    onTrigger: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
};
